import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { selectStyledItems } from 'store/graph/selectors';
import {
  selectAffiliationClusterMode,
  selectAffiliationFilter,
  selectShowDeceasedConnections,
} from 'store/filters/selectors';
import { canAddDeceased } from 'utils/graph-validators';
import CytoscapeChart from './cytoscape-chart';
import { perfMark, perfMeasure } from 'utils/graph-perf';
import { getClusterableAffiliationFromFilter } from 'utils/affiliation';

// Signal-based color mapping
const SIGNAL_COLORS = {
  multi: '#E91E63',
  coattendee: '#FF9800',
  geographic: '#9C27B0',
  default: '#607D8B',
};

// Helper to determine color from signals
const getSuggestedColor = (signals) => {
  if (signals.length > 1) return SIGNAL_COLORS.multi;
  if (signals.includes('coattendee')) return SIGNAL_COLORS.coattendee;
  if (signals.includes('geographic')) return SIGNAL_COLORS.geographic;
  return SIGNAL_COLORS.default;
};

// Helper to create suggested node styling
const getSuggestedNodeStyle = (signals) => {
  const color = getSuggestedColor(signals);
  const isMultiSignal = signals.length > 1;

  return {
    border: {
      color,
      width: isMultiSignal ? 4 : 3,
      style: 'dashed',
    },
    opacity: 0.9,
  };
};

// Helper to create suggested edge styling
const getSuggestedEdgeStyle = (signals) => ({
  color: getSuggestedColor(signals),
  width: 2,
  dash: [8, 6],
});

// Affiliation parsing is shared (audit S10 / H17) — see src/utils/affiliation.js.
const getClusterableAffiliation = getClusterableAffiliationFromFilter;

// items is an object, not array.
// Merge suggested overlays into the flat graph item map before passing it to Cytoscape.
export const ChartWrapper = ({ isCollapsed, suggestedNodes, suggestedEdges }) => {
  const items = useSelector(selectStyledItems);
  const affiliationClusterMode = useSelector(selectAffiliationClusterMode);
  const affiliationFilter = useSelector(selectAffiliationFilter);
  const showDeceasedConnections = useSelector(selectShowDeceasedConnections);

  // Merge suggested nodes and edges into the flat items object
  // Both `items` and suggested items should be flat objects
  const allItems = useMemo(() => {
    perfMark('wrapper:merge:start');
    const hasSuggestedNodes = suggestedNodes && Object.keys(suggestedNodes).length > 0;
    const hasSuggestedEdges = suggestedEdges && Object.keys(suggestedEdges).length > 0;

    // Skip the O(n+e) spread when there is nothing to overlay — preserves the
    // items reference, so downstream memos / setElements diffs short-circuit
    // when only base graph items changed (audit S12).
    if (!hasSuggestedNodes && !hasSuggestedEdges) {
      perfMeasure('wrapper:merge', 'wrapper:merge:start');
      return items;
    }

    const merged = { ...items };

    // Track suggested node IDs that are filtered out (e.g. deceased with toggle OFF)
    // so we can also hide their connected suggested edges.
    const filteredNodeIds = new Set();

    // Add suggested nodes with proper styling.
    // – If the node already exists on the first level (traversal ≤ 1) skip it
    //   entirely; it's already a direct connection — no need for a suggestion.
    // – If it exists at a deeper level (traversal > 1) keep it but apply
    //   suggested styling so the user can see it's been flagged.
    // – Otherwise add it as a brand-new suggested node.
    if (hasSuggestedNodes) {
      for (const nodeId in suggestedNodes) {
        const sugNode = suggestedNodes[nodeId];

        // Apply deceased filter to suggested nodes the same way as regular nodes
        if (!canAddDeceased(sugNode, showDeceasedConnections)) {
          filteredNodeIds.add(nodeId);
          continue;
        }

        const existing = merged[nodeId];

        if (existing) {
          // First-level node — skip entirely, the edge still connects
          if (existing.traversal != null && existing.traversal <= 1) {
            continue;
          }

          // Deeper node — overlay suggested styling onto the existing node
          merged[nodeId] = {
            ...existing,
            ...getSuggestedNodeStyle(sugNode.signals || []),
          };
          continue;
        }

        // Preserve the affiliation grouping field so Cytoscape combo styling can match the base graph.
        // Apply same filter-matching logic as regular nodes so suggested
        // nodes only join a combo when their affiliation matches a selected filter.
        const clusterAffiliation = getClusterableAffiliation(
          sugNode.data?.affliationUnit,
          affiliationFilter,
          affiliationClusterMode,
        );
        const data = clusterAffiliation
          ? { ...sugNode.data, affliationUnit: clusterAffiliation }
          : sugNode.data
            ? { ...sugNode.data, affliationUnit: undefined }
            : undefined;
        merged[nodeId] = {
          ...sugNode,
          data,
          ...getSuggestedNodeStyle(sugNode.signals || []),
          label: { text: sugNode.value || sugNode.label || nodeId },
          size: 4,
          color: '#f0f0f0',
        };
      }
    }
    
    // Add suggested edges with proper styling.
    // Skip edges connected to nodes that were filtered out above.
    if (hasSuggestedEdges) {
      for (const edgeKey in suggestedEdges) {
        const sugEdge = suggestedEdges[edgeKey];
        if (filteredNodeIds.has(sugEdge.id1) || filteredNodeIds.has(sugEdge.id2)) {
          continue;
        }
        merged[edgeKey] = {
          ...sugEdge,
          ...getSuggestedEdgeStyle(sugEdge.signals || []),
        };
      }
    }

    perfMeasure('wrapper:merge', 'wrapper:merge:start');
    return merged;
  }, [items, suggestedNodes, suggestedEdges, affiliationClusterMode, affiliationFilter, showDeceasedConnections]);

  return <CytoscapeChart items={allItems} isCollapsed={isCollapsed} />;
};

ChartWrapper.propTypes = {
  items: PropTypes.object,
  suggestedNodes: PropTypes.object,
  suggestedEdges: PropTypes.object,
};

export default ChartWrapper;
